import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Plus,
  ShieldCheck,
  ArrowRightLeft,
  Send,
  AlertCircle,
  Search,
  Users,
  Clock,
  XCircle,
  CheckCircle2,
  Slash,
} from 'lucide-react';
import {
  api,
  parseApiError,
  type OrganizationRole,
  type OrganizationSummary,
  type DiscoverableOrgSummary,
  type CreateMyOrgResponse,
  type Me,
  type MyRoleRequestSummary,
  type MyClanCreateRequestSummary,
} from '../lib/api';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { PageWrapper } from '../components/PageWrapper';
import { RoleRequestForm } from '../components/RoleRequestForm';
import { RoleBadgeInline } from '../components/OrgPanel';
import { OrgPublicPageView } from '../components/OrgPublicPageView';
import { PresenceBar } from '../components/PresenceBar';
import { useWorkspace } from '../lib/workspace';
import { useEffectiveRole, useEffectiveMe } from '../lib/viewAs';
import { isPlatformOrgManager } from '../lib/orgAccess';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

// ─────────────────────────────────────────────────────────────────────────────
// MyOrg — the user's own org dashboard.
//
// Phase 3.5 — refactored to consume WorkspaceProvider for the active-org
// concept (lifted from this page's local localStorage key into the global
// context shared with active-comp). 0-org branch wires GET /orgs/discover.
// Non-founder org-create submissions write a formal OrgCreationRequest row
// server-side and surface a "request submitted" confirmation here.
//
// Three states:
//   1. User has 0 orgs            → discovery + create-or-request CTA.
//   2. User has 1 org             → that org's dashboard with leave button.
//   3. User has >1 orgs           → "active org" picker + dashboard for selected.
// ─────────────────────────────────────────────────────────────────────────────

// roleIcon + RoleBadgeInline + OrgPanel now live in components/OrgPanel
// (shared with the Setup Organisation step).

function CreateOrgInline({
  isFounder,
  onCreated,
  onRequested,
}: {
  isFounder: boolean;
  onCreated: (orgId: string) => void;
  onRequested: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: api.createMyOrg,
    onSuccess: (data: CreateMyOrgResponse) => {
      setName('');
      setBillingEmail('');
      setReason('');
      setOpen(false);
      setError('');
      if ('status' in data && data.status === 'request_pending') {
        onRequested(data.message);
      } else if ('id' in data) {
        onCreated(data.id);
      }
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`btn-primary ${MORPH} inline-flex items-center gap-2`}
      >
        <Plus className="h-4 w-4" strokeWidth={2.5} />
        {isFounder ? 'Create new org' : 'Request to create org'}
      </button>
    );
  }

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-ink-secondary" />
        <h3 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">
          {isFounder ? 'Create organisation' : 'Request to create organisation'}
        </h3>
      </div>
      {!isFounder && (
        <p className="mb-3 text-xs text-ink-secondary">
          Org creation is founder-only. Submitting writes a formal request that Luke or Lisa will review (max 3 pending requests; same name within 24h of denial is blocked).
        </p>
      )}
      <div className="space-y-3">
        <input
          className="input w-full"
          placeholder="Organisation name"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        {isFounder && (
          <input
            className="input w-full"
            placeholder="Billing email (optional)"
            value={billingEmail}
            onChange={e => setBillingEmail(e.target.value)}
          />
        )}
        {!isFounder && (
          <textarea
            className="input w-full resize-none"
            rows={3}
            placeholder="Why do you want this org? (optional)"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        )}
        {error && (
          <p className="flex items-start gap-2 text-xs text-red-400">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => mut.mutate({
              name: name.trim(),
              billingEmail: billingEmail.trim() || undefined,
              reason: reason.trim() || undefined,
            })}
            disabled={!name.trim() || mut.isPending}
            className={`btn-primary ${MORPH} inline-flex items-center gap-2 disabled:opacity-50`}
          >
            <Send className="h-3.5 w-3.5" strokeWidth={2.5} />
            {mut.isPending ? 'Submitting…' : (isFounder ? 'Create' : 'Submit request')}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); setError(''); }}
            className="px-3 py-2 font-display text-xs font-black uppercase tracking-wider text-ink-meta transition hover:text-ink-secondary"
          >
            Cancel
          </button>
        </div>
      </div>
    </Card>
  );
}

function DiscoverOrgsGrid() {
  const { data, isLoading } = useQuery({
    queryKey: ['discoverOrgs'],
    queryFn: () => api.discoverOrgs(),
  });

  if (isLoading) {
    return (
      <Card>
        <p className="text-xs text-ink-meta">Loading discoverable orgs…</p>
      </Card>
    );
  }

  const orgs = data?.orgs ?? [];

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-ink-secondary" />
        <h2 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">Discover orgs</h2>
      </div>
      {orgs.length === 0 ? (
        <p className="text-xs text-ink-meta">
          No orgs are accepting public requests right now. Ping the org owner directly, or DM Luke / Lisa on Discord.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {orgs.map((org: DiscoverableOrgSummary) => (
            <div
              key={org.orgId}
              className="rounded-lg bg-white/[0.04] p-3"
            >
              <div className="mb-1 flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-ink-secondary" />
                <p className="truncate font-display text-sm font-black uppercase tracking-wide text-ink-primary">
                  {org.name}
                </p>
              </div>
              {org.description && (
                <p className="mb-2 line-clamp-2 text-[11px] text-ink-secondary">{org.description}</p>
              )}
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-meta">
                  <Users className="-mt-0.5 mr-1 inline h-3 w-3" strokeWidth={2.5} />
                  {org.memberCount} members
                </span>
                <button
                  type="button"
                  disabled
                  title="Org not yet joinable from outside — DM Luke / Lisa on Discord"
                  className="inline-flex items-center gap-1 rounded bg-white/[0.05] px-2 py-1 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta"
                >
                  Request to join
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MyRoleRequestsPanel — own pending + recent role-requests with Withdraw.
// ─────────────────────────────────────────────────────────────────────────────

function statusVisuals(status: MyRoleRequestSummary['status']) {
  switch (status) {
    case 'PENDING':   return { Icon: Clock,        cls: 'bg-white/[0.05] text-amber-200' };
    case 'APPROVED':  return { Icon: CheckCircle2, cls: 'bg-white/[0.05] text-emerald-200' };
    case 'DENIED':    return { Icon: XCircle,      cls: 'bg-white/[0.05] text-red-200' };
    case 'WITHDRAWN': return { Icon: Slash,        cls: 'bg-white/[0.05] text-ink-secondary' };
  }
}

function MyRoleRequestsPanel() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['myRoleRequests'],
    queryFn: api.myRoleRequests,
  });
  const [error, setError] = useState('');

  const withdrawMut = useMutation({
    mutationFn: (requestId: string) => api.withdrawRoleRequest(requestId),
    onSuccess: () => {
      setError('');
      queryClient.invalidateQueries({ queryKey: ['myRoleRequests'] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (isLoading) return null;
  const requests = data?.requests ?? [];
  if (requests.length === 0) return null;

  const onWithdraw = (r: MyRoleRequestSummary) => {
    if (!window.confirm(`Withdraw your request for ${r.requestedRole} on ${r.orgId.replace(/^ORG#/, '')}?`)) return;
    withdrawMut.mutate(r.requestId);
  };

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-ink-secondary" />
        <h2 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">Your role-requests</h2>
        {data && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-meta">
            {data.pendingCount}/{data.pendingLimit} pending
          </span>
        )}
      </div>
      {error && (
        <p className="mb-2 flex items-start gap-2 text-xs text-red-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
      <div className="space-y-2">
        {requests.map(r => {
          const { Icon, cls } = statusVisuals(r.status);
          return (
            <div
              key={r.requestId}
              className="rounded-lg bg-white/[0.04] px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${cls}`}>
                  <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
                  {r.status}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-secondary">
                  {r.currentRole} → {r.requestedRole}
                </span>
                {r.scope === 'COMP' && r.scopeCompId && (
                  <span className="font-mono text-[10px] text-ink-meta">on {r.scopeCompId.replace(/^COMP#/, '')}</span>
                )}
                <span className="ml-auto font-mono text-[10px] text-ink-meta">
                  {new Date(r.createdAt).toLocaleDateString()}
                </span>
              </div>
              {r.reason && (
                <p className="mt-1 line-clamp-2 text-[11px] text-ink-secondary">{r.reason}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-meta">
                  {r.orgId.replace(/^ORG#/, '')}
                </span>
                {r.status === 'PENDING' && (
                  <button
                    type="button"
                    onClick={() => onWithdraw(r)}
                    disabled={withdrawMut.isPending}
                    className={`btn-ghost ${MORPH} ml-auto inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-wider disabled:opacity-50`}
                  >
                    Withdraw
                  </button>
                )}
                {r.resolverNote && (
                  <span className="text-[11px] italic text-ink-secondary">"{r.resolverNote}"</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MyClanCreateRequestsPanel — T3 (server-is-the-org, 2026-07-19): the
// requester's own status view for GATE-flow "apply to create a clan"
// submissions (see BuildClanScreen.tsx). Mirrors MyRoleRequestsPanel's
// status-badge idiom exactly (same PENDING/APPROVED/DENIED language) —
// APPROVED here means the request minted a clan and the requester is now its
// leader, so it links straight to My Clan instead of offering a Withdraw
// action (there's no withdraw endpoint for this request type; an org admin's
// approve/deny is the only resolution path, same as role-requests' inbox side).
// ─────────────────────────────────────────────────────────────────────────────

function clanRequestStatusVisuals(status: MyClanCreateRequestSummary['status']) {
  switch (status) {
    case 'PENDING':  return { Icon: Clock,        cls: 'bg-white/[0.05] text-amber-200' };
    case 'APPROVED': return { Icon: CheckCircle2, cls: 'bg-white/[0.05] text-emerald-200' };
    case 'DENIED':   return { Icon: XCircle,      cls: 'bg-white/[0.05] text-red-200' };
  }
}

function MyClanCreateRequestsPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['myClanCreateRequests'],
    queryFn: api.myClanCreateRequests,
  });

  if (isLoading) return null;
  const requests = data?.requests ?? [];
  if (requests.length === 0) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <Users className="h-4 w-4 text-ink-secondary" />
        <h2 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">Your clan applications</h2>
      </div>
      <div className="space-y-2">
        {requests.map(r => {
          const { Icon, cls } = clanRequestStatusVisuals(r.status);
          return (
            <div key={r.requestId} className="rounded-lg bg-white/[0.04] px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${cls}`}>
                  <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
                  {r.status}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-secondary">{r.name}</span>
                <span className="ml-auto font-mono text-[10px] text-ink-meta">
                  {new Date(r.createdAt).toLocaleDateString()}
                </span>
              </div>
              {r.description && (
                <p className="mt-1 line-clamp-2 text-[11px] text-ink-secondary">{r.description}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-meta">
                  {r.orgId.replace(/^ORG#/, '')}
                </span>
                {r.status === 'DENIED' && r.resolverNote && (
                  <span className="text-[11px] italic text-ink-secondary">"{r.resolverNote}"</span>
                )}
                {r.status === 'APPROVED' && (
                  <Link
                    to="/leader"
                    className={`btn-ghost ${MORPH} ml-auto inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-wider`}
                  >
                    Go to your clan
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// IncomingRoleRequestsPanel now lives in components/IncomingRoleRequestsPanel
// (shared with the Setup Organisation step).

// CreateCompetitionPanel removed — competition creation lives in the Setup
// Competition step (Run It → Setup).

// ─────────────────────────────────────────────────────────────────────────────
// OrgInfoSection — the org's information page (hero, about, rules, sponsors,
// FAQ, staff, contact), rendered from the same published blob as /org/:slug.
// Server-side Discord-guild gate applies to the fetch; content is filled out
// in Setup → Organisation (D9 — this page is view-only).
// ─────────────────────────────────────────────────────────────────────────────

function OrgInfoSection({ slug, canEdit }: { slug?: string; canEdit: boolean }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['publicOrgPage', slug],
    queryFn: () => api.publicOrgPage(slug!),
    enabled: !!slug,
    retry: false,
  });

  if (!slug) {
    return (
      <Card>
        <p className="text-sm text-ink-secondary">This org has no page slug yet — it can't render an info page.</p>
      </Card>
    );
  }
  if (isLoading) return <PageSpinner />;
  if (error || !data) {
    // apiFetch throws "<server text> (HTTP <status>)" — match on the status.
    const msg = (error as Error | null)?.message ?? '';
    const notPublished = msg.includes('(HTTP 404)');
    const restricted   = msg.includes('(HTTP 403)');
    const unauthed     = msg.includes('(HTTP 401)');
    return (
      <Card>
        <p className="text-sm text-ink-secondary">
          {notPublished && "This org's information page hasn't been published yet."}
          {restricted && "This page is restricted to members of the org's Discord server — check the org has a Discord server bound and that you're in it."}
          {unauthed && 'Your session has dropped — sign in again via /ggportal.'}
          {!notPublished && !restricted && !unauthed && 'Could not load the org information page.'}
          {notPublished && canEdit && (
            <>
              {' '}Fill it out in <Link to="/admin/setup" className="text-accent-500 hover:underline">Setup → Organisation</Link> (Org page editor) and tick Published.
            </>
          )}
        </p>
        {!notPublished && msg && (
          <p className="mt-2 font-mono text-[10px] text-ink-meta">{msg}</p>
        )}
      </Card>
    );
  }
  return <OrgPublicPageView data={data} />;
}

function MyOrgIntro({ hasOrg, myOrgRole }: { hasOrg: boolean; myOrgRole?: OrganizationRole }) {
  const { data: me } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me });
  const effectiveRole = useEffectiveRole(me?.role);
  const isFounder = effectiveRole === 'founder';
  const isOrgOwner = myOrgRole === 'ORG_OWNER';
  const isOrgAdmin = myOrgRole === 'ORG_ADMIN';

  const intro = !hasOrg
    ? (isFounder
        ? "Founder view: you can immediate-create any org. Non-founders write a request that you (or Lisa) approve via the Org Requests inbox."
        : "Organisations are the top of the chain — they own competitions and run their own admins, members and clans. Request to create one or wait for a founder to add you to an existing one.")
    : (isFounder
        ? "Founder view: full access. Switch ‘view as’ in the bottom-left to preview a lower platform role, or pick ‘Org admin (scoped)’ to see exactly what a real org's ORG_ADMIN sees — there's no separate simulation for ORG_OWNER."
        : isOrgOwner
        ? "You own this org. You can appoint admins (subject to a 4-seat cap), create competitions, and approve incoming role requests."
        : isOrgAdmin
        ? "You’re an admin here. You can manage members and run day-to-day org operations; promotions to ORG_OWNER are founder-only."
        : "You’re part of this org. Comp-level work happens under ‘My Comp’ — apply to help on a comp run by this org if you want to do more.");

  const points = !hasOrg
    ? [
        { text: 'Orgs own competitions, clans, and the people running them.' },
        { text: 'Once you’re in an org, ‘My Comp’ unlocks creating or applying to competitions.' },
      ]
    : isFounder || isOrgOwner
    ? [
        { text: 'Promotions: org owner appoints admins (max 4), org owner appoints comp owners, founders alone appoint org owners.' },
        { text: 'Incoming role requests appear here — approve or deny with optional notes.' },
        { text: 'Use the Create competition button to spin up a new comp under this org.' },
      ]
    : isOrgAdmin
    ? [
        { text: 'You can manage org members but can’t promote anyone to ORG_OWNER (founder-only).' },
        { text: 'Members under you can apply to comps — review applications via the inbox.' },
      ]
    : [
        { text: 'Want a bigger role? Use ‘Request elevation’ to ask an owner.' },
        { text: 'Day-to-day comp work lives under My Comp.' },
      ];

  return <HelperCard id={hasOrg ? 'myorg-intro-has' : 'myorg-intro-empty'} title="What lives here" intro={intro} points={points} />;
}

export function MyOrg() {
  const queryClient = useQueryClient();
  const {
    activeOrgId,
    setActiveOrgId,
    memberships,
    meLoading,
  } = useWorkspace();
  const { data: meForViewAs } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me });
  const effectiveRole = useEffectiveRole(meForViewAs?.role);
  const isFounder = effectiveRole === 'founder';
  // Org admins shouldn't be pitched a new org (Lisa, 2026-07-17) — hides the
  // "request/create new org" CTA (CreateOrgInline, 0-orgs branch below)
  // whenever the EFFECTIVE identity holds an ORG_ADMIN membership. Reads
  // effectiveMe (view-as-aware): a real ORG_ADMIN already never reaches the
  // 0-orgs branch (their real membership count is always >= 1), so this
  // mainly corrects a founder previewing "Org admin (scoped)" — that
  // simulation stamps a real membership shape ({role:'member',
  // orgMemberships:[{orgId:X, role:'ORG_ADMIN'}]}, see viewAs.ts's
  // useEffectiveMe), and this page's `memberships` (from useWorkspace, NOT
  // view-as-aware) stays the founder's real (often zero) rows — without this
  // check, that preview would incorrectly show the founder's own CTA.
  const effectiveMeForOrgCreate = useEffectiveMe(meForViewAs);
  const isEffectiveOrgAdmin = (effectiveMeForOrgCreate?.orgMemberships ?? []).some(m => m.role === 'ORG_ADMIN');
  const [pendingNotice, setPendingNotice] = useState<string | null>(null);
  const [roleRequestModalOrg, setRoleRequestModalOrg] = useState<{
    orgId: string;
    orgName?: string;
    currentRole: OrganizationRole;
  } | null>(null);

  // Hydrate org details (name etc.) for the membership list. WorkspaceProvider
  // hydrates `activeOrg` for the currently-selected org, but the picker also
  // wants names for non-active memberships. The same `['orgs']` cache is used.
  const { data: orgsList, isLoading: orgsLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: api.orgs,
    // Founders may hold no membership rows but still need the org list to
    // drive the founder-only org switcher on the info page (D1).
    enabled: memberships.length > 0 || isFounder,
  });

  const orgsById = new Map<string, OrganizationSummary>(
    (orgsList ?? []).map(o => [o.id, o])
  );

  useEffect(() => {
    document.title = 'My Org · GG Anvil';
  }, []);

  const onSwitch = (orgId: string) => {
    setActiveOrgId(orgId);
  };

  // onLeft (leave-org invalidations) moved with OrgPanel to the Setup
  // Organisation step; reinstate when a leave affordance returns here.

  const onCreated = (orgId: string) => {
    queryClient.invalidateQueries({ queryKey: ['myOrgs'] });
    queryClient.invalidateQueries({ queryKey: ['orgs'] });
    queryClient.invalidateQueries({ queryKey: ['me'] });
    setActiveOrgId(orgId);
  };

  const onRequested = (message: string) => {
    setPendingNotice(message);
    queryClient.invalidateQueries({ queryKey: ['myOrgCreationRequests'] });
  };

  if (meLoading || orgsLoading) return <PageSpinner />;

  // ── 0-orgs state ──────────────────────────────────────────────────────────
  // Founders fall through even with zero membership rows — they view any
  // org's info page via the founder switcher (D1).
  if (memberships.length === 0 && !(isFounder && (orgsList ?? []).length > 0)) {
    return (
      <PageWrapper maxWidth="3xl" spacing="md">
        <div className={`${SETTLE} flex items-center gap-3`} style={settleDelay(0)}>
          <Building2 className="h-6 w-6 text-ink-secondary" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight text-ink-primary">My Org</h1>
        </div>

        <div className={SETTLE} style={settleDelay(1)}>
          <MyOrgIntro hasOrg={false} myOrgRole={undefined} />
        </div>

        <div className={SETTLE} style={settleDelay(2)}>
          <EmptyState
            title="No org yet"
            message="You're not a member of any organisation. Browse below or request to create one."
          />
        </div>

        {pendingNotice && (
          <Card>
            <p className="rounded bg-white/[0.05] px-3 py-2 text-xs text-amber-200">{pendingNotice}</p>
          </Card>
        )}

        <div className={SETTLE} style={settleDelay(3)}>
          <MyRoleRequestsPanel />
          <MyClanCreateRequestsPanel />
        </div>

        <div className={SETTLE} style={settleDelay(4)}>
          <DiscoverOrgsGrid />
        </div>

        {/* Org admins shouldn't be pitched a new org — see isEffectiveOrgAdmin's
            doc comment above. */}
        {!isEffectiveOrgAdmin && (
          <div className={SETTLE} style={settleDelay(5)}>
            {/* Duplicate-org warning (T07 seam): the Discord server this
                session entered through may already have an org — either a
                bot-install auto-create (server-as-org) or one bound later via
                GuildConnectionPanel. Without this, a portal-created org here
                is a silent serverless duplicate of that org. Only renders
                once /me resolves entryOrgId against a real, still-existing
                ORG# row (see portalApi's GET /me) — deliberate creation below
                is still allowed, this is a notice, not a block. */}
            {meForViewAs?.entryOrgId && meForViewAs?.entryOrgName && (
              <Card className="mb-3">
                <div className="flex items-start gap-3">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" />
                  <div>
                    <p className="text-xs text-ink-body">
                      The Discord server you signed in through already has an organisation —{' '}
                      <strong className="text-ink-primary">{meForViewAs.entryOrgName}</strong>. You probably want
                      to open that org instead of creating a new one.
                    </p>
                    <Link
                      to={`/org/${meForViewAs.entryOrgId}`}
                      className="mt-2 inline-flex items-center gap-1 font-display text-[11px] font-black uppercase tracking-wider text-accent-500 hover:text-accent-400"
                    >
                      Open {meForViewAs.entryOrgName} →
                    </Link>
                  </div>
                </div>
              </Card>
            )}
            <CreateOrgInline isFounder={isFounder} onCreated={onCreated} onRequested={onRequested} />
          </div>
        )}
      </PageWrapper>
    );
  }

  // ── 1+ orgs state (or founder with zero memberships) ──────────────────────
  const activeMembership =
    memberships.find(m => m.orgId === activeOrgId) ?? memberships[0];

  // The org whose info page renders below. Founders resolve from the full org
  // list (switchable, D1); everyone else is locked to their membership org.
  const infoOrg = isFounder
    ? ((orgsList ?? []).find(o => o.id === activeOrgId)
        ?? (activeMembership ? orgsById.get(activeMembership.orgId) : undefined)
        ?? (orgsList ?? [])[0])
    : (activeMembership ? orgsById.get(activeMembership.orgId) : undefined);
  // Platform-tier bypass routed through isPlatformOrgManager (2026-07-24,
  // dormant platform-admin lockout closure) — the contract is founder OR
  // (platform) admin, not founder-only; a raw isFounder check here locked
  // platform Admins out of editing org info the backend already lets them
  // manage. NOTE: this deliberately does NOT touch the shared `isFounder`
  // above (org-switcher / create-org CTA gating stay founder-only, out of
  // this rewire's scope) — only this specific edit-info bypass widens.
  const canEditInfo = isPlatformOrgManager(effectiveMeForOrgCreate)
    || activeMembership?.role === 'ORG_OWNER'
    || activeMembership?.role === 'ORG_ADMIN';

  return (
    <PageWrapper maxWidth="6xl" spacing="md">
      <div className={`${SETTLE} flex items-center justify-between gap-3`} style={settleDelay(0)}>
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-ink-secondary" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight text-ink-primary">My Org</h1>
        </div>
        {/* Presence face-pile — keyed to the org currently on-screen (infoOrg,
            a prefixed 'ORG#...' resourceKey). Awareness only; renders nothing
            when nobody else is looking at this org right now. */}
        <PresenceBar resourceKey={infoOrg?.id} />
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <MyOrgIntro hasOrg={true} myOrgRole={activeMembership?.role} />
      </div>

      {isFounder && (orgsList ?? []).length > 1 && (
        <Card>
          <label className="label">Viewing org (founder)</label>
          <select
            value={infoOrg?.id ?? ''}
            onChange={e => setActiveOrgId(e.target.value)}
            className="input w-full max-w-sm"
          >
            {(orgsList ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Card>
      )}

      {pendingNotice && (
        <Card>
          <p className="rounded bg-white/[0.05] px-3 py-2 text-xs text-amber-200">{pendingNotice}</p>
        </Card>
      )}

      {memberships.length > 1 && (
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-ink-secondary" />
            <h2 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">Switch active org</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {memberships.map(m => {
              const org = orgsById.get(m.orgId);
              const isActive = m.orgId === activeMembership.orgId;
              return (
                <button
                  key={m.orgId}
                  type="button"
                  onClick={() => onSwitch(m.orgId)}
                  className={
                    `${MORPH} inline-flex items-center gap-2 rounded-lg px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider transition ` +
                    (isActive
                      ? 'bg-white/[0.07] text-accent-500'
                      : 'bg-white/[0.04] text-ink-secondary hover:bg-white/[0.06] hover:text-ink-primary')
                  }
                >
                  <Building2 className="h-3 w-3" strokeWidth={2.5} />
                  <span className="truncate max-w-[160px]">{org?.name ?? m.orgId.replace(/^ORG#/, '')}</span>
                  <RoleBadgeInline role={m.role} />
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* The org information page (D9/D10): view-only render of the published
          org page — filled out in Setup → Organisation. */}
      <OrgInfoSection slug={infoOrg?.slug} canEdit={canEditInfo} />

      <MyRoleRequestsPanel />
      <MyClanCreateRequestsPanel />

      {roleRequestModalOrg && (
        <RoleRequestForm
          open={true}
          onClose={() => setRoleRequestModalOrg(null)}
          orgId={roleRequestModalOrg.orgId}
          orgName={roleRequestModalOrg.orgName}
          currentOrgRole={roleRequestModalOrg.currentRole}
        />
      )}
    </PageWrapper>
  );
}
